import { timetableAPI, timetableBeta } from '@/api'
import { filterForReports } from '@/helpers/filterForJornal'
import { ITimetableReportItem } from '@pages/Journal2/components/Tabs/KTPJournal'
import { ILessonReportData } from './types'
import { Dayjs } from 'dayjs'
import timetableReportStatusEnum from '@/constant/timetableReportStatusEnum'

export const fetchTimetableReports = async (
  perPage: Number = 20,
  page: number = 1,
  params = {},
): Promise<ITimetableReportItem[]> => {
  try {
    const res = await timetableAPI.conference({
      'per-page': perPage,
      page: page,
      ...params,
    })
    const reports = res?.data.data ?? []

    return filterForReports(reports)
  } catch (error) {
    console.error('Ошибка загрузки: fetchTimetableReports', error)
    return []
  }
}

export const makeEnteringToClassroom = async (
  report: ITimetableReportItem,
): Promise<string | null> => {
  try {
    const res = await timetableAPI.saveTimeOpeningLessonFirstTime({
      id: report.id,
    })

    return res?.data?.entry_time ?? null
  } catch (error) {
    console.error('Ошибка makeEnteringToClassroom:', error)
    return null
  }
}

export const makeLessonCompleted = async (
  reportId: number,
  comment: string,
  date: string,
  lessonTime: number,
  shouldSetJournalStatus?: boolean,
  revokeGroupAccessExtension?: boolean,
): Promise<boolean> => {
  try {
    const res = await timetableAPI.done({
      id: reportId,
      data: {
        comment: comment,
        date_conference: date,
        lesson_time_change: lessonTime,
        ...(shouldSetJournalStatus !== undefined && {
          set_journal_status: shouldSetJournalStatus ? 1 : 0,
        }),
        ...(revokeGroupAccessExtension !== undefined && {
          revoke_group_access_extension: revokeGroupAccessExtension ? 1 : 0,
        }),
      },
    })

    return res.data.status === 'success'
  } catch (error) {
    console.error('Ошибка makeLessonCompleted:', error)
    return false
  }
}

/**
 * Перенос вводного занятия: исходное занятие остаётся в расписании со статусом
 * «Перенесено», а на новую дату создаётся отдельное занятие. Поэтому используется
 * специальный endpoint, а не CHANGE_DATE_TIME (тот переносит само занятие).
 */
export const rescheduleIntroductoryLesson = async (
  reportId: number,
  comment: string,
  date: Dayjs,
  startedAt: Dayjs,
  endedAt: Dayjs,
): Promise<boolean> => {
  try {
    const res = await timetableAPI.rescheduleIntroductory({
      id: reportId,
      comment: comment,
      date_conference: date.format('YYYY-MM-DD'),
      start_time: startedAt.format('HH:mm'),
      end_time: endedAt.format('HH:mm'),
    })

    return res.data.status === 'success'
  } catch (error) {
    console.error('Ошибка rescheduleIntroductoryLesson:', error)
    return false
  }
}

export const rescheduleLesson = async (
  reportId: number,
  comment: string,
  date: Dayjs,
  startedAt: Dayjs,
  endedAt: Dayjs,
  shouldSetJournalStatus?: boolean,
): Promise<boolean> => {
  try {
    const res = await timetableBeta.edit({
      data: {
        report_id: reportId,
        apply_next: false,
        comment: comment,
        date_conference: date.format('YYYY-MM-DD'),
        start_time: startedAt.format('HH:mm'),
        end_time: endedAt.format('HH:mm'),
        status: timetableReportStatusEnum.rescheduled,
        type: 'CHANGE_DATE_TIME',
        ...(shouldSetJournalStatus !== undefined && {
          set_journal_status: shouldSetJournalStatus ? 1 : 0,
        }),
      },
    })

    return res.data.status === 'success'
  } catch (error) {
    console.error('Ошибка makeLessonCompleted:', error)
    return false
  }
}

export const makeLessonCancel = async (
  reportId: number,
  comment: string,
  status: string,
  shouldSetJournalStatus?: boolean,
  extendGroupAccess?: boolean,
  onError?: (message: string) => void,
): Promise<boolean> => {
  try {
    const cancelTypeMap: Record<string, string> = {
      '10': 'student',
      '11': 'seminarian',
      '12': 'paid',
    }

    const res = await timetableAPI.cancelReport({
      params: { id: reportId },
      data: {
        comment,
        cancel_type: cancelTypeMap[status] || 'unknown',
        ...(shouldSetJournalStatus !== undefined && {
          set_journal_status: shouldSetJournalStatus ? 1 : 0,
        }),
        ...(extendGroupAccess !== undefined && {
          extend_group_access: extendGroupAccess ? 1 : 0,
        }),
      },
    })

    return res.data.status === 'success'
  } catch (error) {
    console.error('Ошибка makeLessonCancel:', error)
    // Бэкенд кладёт ошибки формы как error: [{ поле: ['текст'] }] — берём первый текст
    const message = [(error as any)?.response?.data?.error]
      .flat(3)
      .flatMap((item) => (item && typeof item === 'object' ? Object.values(item).flat() : [item]))
      .find((item) => typeof item === 'string' && item)
    if (message) onError?.(message)
    return false
  }
}

export const makeMassLessonReport = async (
  reports: ILessonReportData[],
): Promise<boolean> => {
  try {
    const res = await timetableAPI.massReport(reports)

    return res.data.status === 'success'
  } catch (error) {
    console.error('Ошибка makeMassLessonReport:', error)
    return false
  }
}
